import {
  Box,
  chakra,
  Flex,
  Grid,
  GridItem,
  Link,
  Spacer,
  Tooltip,
  useStyleConfig,
  VStack,
} from '@chakra-ui/react';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { NavLink } from 'react-router-dom';

import {
  filterNavItemsForUser,
  type NavItemConfig,
  type RouteNavConfig,
} from '@revelio/auth';
import { TourClasses, useTourStore } from '@revelio/core';

import { useHideNav } from '../../../shared/hooks/useHideNav';
import { Logo } from './Logo';
import { AccountMenu } from './menus/account-menu';
import { MethodologyMenu } from './menus/methodology-menu';
import { NavIconChild, navIconBg } from './nav-icon';
import styles from './side-nav.module.css';
import { SubNav } from './sub-nav/sub-nav';
import { useEnforceUserIntroduction } from './useEnforceUserIntroduction';

export interface WithSpotlightWrapperProps {
  condition: boolean;
  children: JSX.Element;
}

const WithSpotlightWrapper = ({
  condition,
  children,
}: WithSpotlightWrapperProps) => {
  return condition ? (
    <Flex
      className={TourClasses.TOUR_CONTROL_PANEL_CLASS}
      direction="column"
      alignItems="center"
      justifyContent="flex-start"
      w="100%"
      rowGap="5px"
    >
      {children}
    </Flex>
  ) : (
    children
  );
};
export interface SideNavProps {
  items: (NavItemConfig | RouteNavConfig)[];
  version?: string;
  onOpenImpersonationModal?: () => void;
}

export function SideNav(props: SideNavProps) {
  const linkStyles = useStyleConfig('Link', { variant: 'nav' });

  const [{ pagesBeforeDivider, pagesAfterDivider }, setPages] = useState<
    Record<string, RouteNavConfig[]>
  >({
    pagesBeforeDivider: [],
    pagesAfterDivider: [],
  });

  const { loggedInUser, fetching } = useEnforceUserIntroduction();

  const isTour = useTourStore((s) => s.isTour);

  useEffect(() => {
    if (fetching || isEmpty(loggedInUser)) {
      return;
    }

    const userTabs = loggedInUser.tabs || [];
    const userRole = loggedInUser.role;

    const pages = filterNavItemsForUser(props.items, userTabs, userRole).reduce(
      (acc, page) => {
        if (page.excludeFromSideNavButtons) return acc;

        return [...acc, page];
      },
      [] as RouteNavConfig[]
    );

    const pagesBeforeDivider = pages.filter((page) => page.isSourcesTab);
    const pagesAfterDivider = pages.filter((page) => !page.isSourcesTab);

    setPages({ pagesBeforeDivider, pagesAfterDivider });
  }, [fetching, loggedInUser, props.items]);

  const renderPageTabs = (pages: RouteNavConfig[]) => {
    return pages.map((item) =>
      !item.children ? (
        // biome-ignore lint/style/noNestedTernary: migrated from develop
        item.element ? (
          <Link
            key={item.name}
            className={classNames('nav', styles.navLink)}
            as={NavLink}
            to={item.path || ''}
            bgImg={navIconBg(item.icon)}
            variant="nav"
            position="relative"
          >
            <NavIconChild icon={item.icon} />
          </Link>
          // biome-ignore lint/style/noNestedTernary: migrated from develop
        ) : item.elementId && !item.path ? (
          // this is when the button is controlled by something external (e.g. jira script in html script)
          <Tooltip hasArrow placement="right" key={item.name} label={item.name}>
            <chakra.a
              key={item.name}
              sx={linkStyles}
              className={classNames('nav', styles.navLink)}
              id={item.elementId}
              bgImg={navIconBg(item.icon)}
              position="relative"
            >
              <NavIconChild icon={item.icon} />
            </chakra.a>
          </Tooltip>
        ) : (
          <chakra.a
            key={item.name}
            sx={linkStyles}
            className={classNames('nav', styles.navLink)}
            href={item.path}
            bgImg={navIconBg(item.icon)}
            position="relative"
          >
            <NavIconChild icon={item.icon} />
          </chakra.a>
        )
      ) : (
        <SubNav
          key={item.name}
          parent={item}
          items={item.children as RouteNavConfig[]}
        />
      )
    );
  };

  const navigate = useNavigate();

  const hideNav = useHideNav();
  if (hideNav) {
    return (
      <Box width="100%" height="100vh">
        <Outlet />
      </Box>
    );
  }

  return (
    <Grid
      className={styles.railGrid}
      templateRows="1"
      templateColumns="var(--rl-rail-w, 54px) 1fr"
    >
      <GridItem
        w="var(--rl-rail-w, 54px)"
        className="rl-sidenav"
        position="relative"
      >
        <VStack
          className={styles.sidebar}
          align="center"
          height="100vh"
          spacing={5}
        >
          <Box
            data-testid="sidebar-logo"
            className={styles.logoBox}
            height="60px"
            width="44px"
            paddingEnd="14px"
            transform="scale(0.9)"
            onClick={() => navigate('/')}
            cursor="pointer"
            transition="opacity 0.1s ease-in-out"
            _hover={{ opacity: 0.7 }}
          >
            <Logo />
          </Box>

          <WithSpotlightWrapper condition={isTour}>
            {/* @ts-expect-error noUncheckedIndexedAccess */}
            <>{renderPageTabs(pagesBeforeDivider)}</>
          </WithSpotlightWrapper>

          {/* @ts-expect-error noUncheckedIndexedAccess */}
          {renderPageTabs(pagesAfterDivider)}
          <Spacer />
          <MethodologyMenu />
          <AccountMenu
            version={`v${props.version}`}
            onOpenImpersonationModal={props.onOpenImpersonationModal}
          />
        </VStack>
      </GridItem>
      <GridItem>
        <Outlet></Outlet>
      </GridItem>
    </Grid>
  );
}

export default SideNav;
